import { useState, useEffect, useRef, type ReactNode } from 'react';
import { Check, Loader2 } from 'lucide-react';

import { cn } from '@/shared/lib/utils';

const BASE =
  'phx-btn inline-flex items-center gap-phx-3 h-phx-control px-phx-5 text-phx-md font-medium ' +
  'rounded-phx border transition-[background-color,border-color,color] ' +
  'focus-visible:outline-none focus-visible:shadow-phx-focus';

const IDLE =
  'bg-phx-surface text-phx-text border-phx-border-2 hover:bg-phx-surface-2 ' +
  'hover:border-phx-border-3 active:bg-phx-surface-3 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const RUNNING = 'bg-phx-surface-2 border-phx-border text-phx-text-muted cursor-wait';
const DONE = 'bg-phx-success-tint border-phx-success text-phx-success';

export interface AsyncActionButtonProps {
  /** External control of the running state. The true → false edge triggers
   *  the "done" indicator animation. */
  isRunning: boolean;
  onClick: () => void;
  /** Idle-state content (icon, label, badge, …). */
  children: ReactNode;
  runningLabel?: ReactNode;
  doneLabel?: ReactNode;
  /** How long the done state lingers (ms). */
  doneDuration?: number;
  className?: string;
  disabled?: boolean;
}

export function AsyncActionButton({
  isRunning,
  onClick,
  children,
  runningLabel = 'Probíhá...',
  doneLabel = 'Hotovo',
  doneDuration = 2000,
  className,
  disabled,
}: AsyncActionButtonProps) {
  const [showDone, setShowDone] = useState(false);
  const prevIsRunningRef = useRef(false);
  const doneTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const wasJustDone = prevIsRunningRef.current && !isRunning;
    prevIsRunningRef.current = isRunning;
    if (wasJustDone) {
      if (doneTimerRef.current) clearTimeout(doneTimerRef.current);
      setShowDone(true);
      doneTimerRef.current = setTimeout(() => {
        setShowDone(false);
        doneTimerRef.current = null;
      }, doneDuration);
    }
  }, [isRunning, doneDuration]);

  useEffect(
    () => () => {
      if (doneTimerRef.current) clearTimeout(doneTimerRef.current);
    },
    []
  );

  if (isRunning) {
    return (
      <button type="button" disabled aria-busy="true" className={cn(BASE, RUNNING)}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        {runningLabel}
      </button>
    );
  }

  if (showDone) {
    return (
      <button type="button" disabled className={cn(BASE, DONE)}>
        <Check className="animate-save-pop h-3.5 w-3.5" aria-hidden="true" />
        {doneLabel}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(BASE, IDLE, className)}
    >
      {children}
    </button>
  );
}
